import { type ReactNode, useEffect, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { MakyMonogram } from "@/components/brand/MakyMark";
import { WorkspaceSwitcher } from "@/components/workspace/WorkspaceSwitcher";
import { useAdminMe, useMe } from "@/lib/api/queries";
import { visibleTabs } from "@/lib/tabs";

export function AppShell({ title, children }: { title: string; children: ReactNode }) {
  // 어드민만 지표 화면으로 가는 입구가 보인다. 화이트리스트 판단은 서버가 하고
  // (`/admin/me` 가 어드민이 아니면 403), 여기서는 성공했을 때만 링크를 건다.
  // 목록을 프론트에 복사하지 않는다 — 두 벌이 되는 순간 반드시 어긋난다.
  const { data: admin } = useAdminMe();
  // 감춘 탭. 못 읽으면(로그인 직후·네트워크 실패) 전부 보인다 — 설정을 못 읽었다고
  // 화면에서 기능이 사라지는 쪽이 훨씬 나쁘다.
  const { data: me } = useMe();
  const tabs = visibleTabs(me?.hidden_tabs);

  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col">
      {/* 탭 여섯 개를 매번 탭 키로 지나가지 않고 본문으로 간다. 포커스를 받기
          전까지는 화면 밖에 있다. */}
      <a href="#maky-main" className="maky-skip text-label-1 font-semibold">
        본문으로 건너뛰기
      </a>

      {/* 떠 있는 헤더. 본문이 아래로 흘러 들어가고, 경계는 실선이 아니라
          짧은 그라디언트다(`.maky-appbar`). */}
      <header className="maky-appbar px-6 pt-4">
        <div className="flex items-center gap-2">
          <MakyMonogram size={26} className="shrink-0" />
          {/* 워크스페이스는 "지금 어디에 있나" 라서 제목보다 위에 온다.
              제목은 본문 첫 줄로 내려갔다 — 탭 줄이 이미 같은 이름을 말하고
              있어서 헤더에 한 번 더 쓰면 같은 말이 두 번 붙는다. */}
          <div className="min-w-0 flex-1">
            <WorkspaceSwitcher />
          </div>
          {admin && (
            <NavLink
              to="/admin"
              data-press="scale"
              className="inline-flex min-h-[44px] shrink-0 items-center rounded-md px-2 text-label-2 text-label-alternative"
            >
              지표
            </NavLink>
          )}
          {/* 설정 입구는 탭 줄 밖에 있다 — 탭을 전부 감춰도 되돌릴 길이 남는다. */}
          <NavLink
            to="/settings"
            data-press="scale"
            className="inline-flex min-h-[44px] shrink-0 items-center rounded-md px-2 text-label-2 text-label-alternative"
          >
            설정
          </NavLink>
        </div>

        {/* 탭을 전부 감추면 이 줄이 사라진다. 설정으로 돌아갈 입구는 위에 남아 있다. */}
        {tabs.length > 0 && <TabRail tabs={tabs} />}
      </header>

      <main id="maky-main" className="flex-1 px-6 pt-4 pb-12">
        {/* 화면 이름은 여기 한 번만 산다. 헤더가 스크롤에 남고 이 제목은
            올라가는, 탭 바 + 큰 제목 구조다. */}
        <h1 className="text-title-3 font-bold text-label-normal">{title}</h1>
        <div className="mt-4">{children}</div>
      </main>
    </div>
  );
}

/** 한 줄로 흐르는 탭. 두 줄로 접히면 화면마다 헤더 높이가 달라져서 본문이
 *  들썩인다 — 접지 않고 가로로 흘리고, 고른 탭을 보이는 자리로 데려온다. */
function TabRail({ tabs }: { tabs: ReturnType<typeof visibleTabs> }) {
  const railRef = useRef<HTMLElement>(null);
  const { pathname } = useLocation();

  useEffect(() => {
    const active = railRef.current?.querySelector<HTMLElement>('[aria-current="page"]');
    if (!active) return;
    // 여섯 번째 탭이 화면 밖에 있으면 지금 어디인지 알 수 없다. 움직임을 줄여
    // 달라고 한 사용자에게는 미끄러뜨리지 않고 바로 옮긴다.
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    active.scrollIntoView({
      behavior: reduce ? "auto" : "smooth",
      inline: "center",
      block: "nearest",
    });
  }, [pathname]);

  return (
    <nav ref={railRef} aria-label="기능" className="maky-tabs mt-1">
      {tabs.map((tab) => (
        <NavLink
          key={tab.to}
          to={tab.to}
          end={tab.to === "/"}
          data-press="scale"
          className={({ isActive }) =>
            isActive
              ? "maky-tab text-label-1 font-semibold text-primary"
              : "maky-tab text-label-1 text-label-alternative"
          }
        >
          {tab.label}
        </NavLink>
      ))}
    </nav>
  );
}

export default AppShell;
